/**
 * 角色 RolesTab (S10.1, rbacV2-upgraded in S2.2) — the role-permission matrix
 * (Board 6).
 *
 * Permission rows (grouped by module) × role columns. The admin preset is
 * locked (always all-permissions). Edit mode toggles the checkboxes; Save is
 * reason-gated (ReasonRequiredModal → audit row) and reprojects every member's
 * effective permissions server-side. This matrix is the single source the route
 * guard reads from (routePermissions.ts / PERMISSION_CATALOG).
 *
 * Two render paths behind `governance.rbacV2` (S2.2):
 *   OFF — the legacy flat checkbox grid below, byte-equivalent to pre-rbacV2
 *         (zero visible change until the flag flips).
 *   ON  — `PermissionMatrixGrid`: module-grouped CRUD matrix with LIVE
 *         inherited-cell signals fed by `resolveEffectiveAccess().sources`,
 *         plus the per-role `inheritsFrom` multi-select. Only direct grants
 *         are editable; the save also carries `inheritsFrom` (one audit row).
 *
 * Save flow (S3.3): with rbacV2 ON, Save first opens `PermissionImpactModal`
 * — the effective added/removed diff per impacted role + the affected-member
 * count, computed over the SAME `overlayDraft` the matrix renders from —
 * then Continue hands off to the existing `ReasonRequiredModal` gate and the
 * unchanged reason-gated audit write. Flag OFF keeps today's Save → reason
 * flow byte-identical (no impact step).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { App, Button, Checkbox, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EditOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import TableContainer, { ListEmptyState } from '@/components/common/TableContainer';
import ReasonRequiredModal from '@/components/common/ReasonRequiredModal';
import { controlHeightTokens } from '@/styles/tokens';
import { PERMISSION_CATALOG } from '@/constants/routePermissions';
import { useFeature } from '@/features/flags';
import PermissionMatrixGrid, { overlayDraft, permissionLabelKey } from './PermissionMatrixGrid';
import PermissionImpactModal from './PermissionImpactModal';
import { useRoles, useSystemMutations, useUsers } from './useSystemGovernance';
import type { GovernanceRole } from './model';

const { Text } = Typography;

interface PermissionRow {
  key: string;
  permission: string;
  moduleKey: string;
}

const PERMISSION_ROWS: PermissionRow[] = PERMISSION_CATALOG.flatMap((module) =>
  module.permissions.map((permission) => ({
    key: permission,
    permission,
    moduleKey: module.moduleKey,
  }))
);

const sortedJson = (values: Iterable<string>): string => JSON.stringify([...values].sort());

const RolesTab = () => {
  const { t } = useTranslation('governance');
  const { message } = App.useApp();
  const { roles, loading, refresh } = useRoles();
  // Users feed the S3.3 impact modal's affected-member count (rbacV2 path only;
  // the cached read is shared with UsersTab).
  const { users } = useUsers();
  const { setRolePermissions, setRoleDefinition } = useSystemMutations();
  const rbacV2 = useFeature('governance.rbacV2');

  const [editMode, setEditMode] = useState(false);
  /** S3.3 — the pre-save impact diff (rbacV2 only; Continue → reason gate). */
  const [impactOpen, setImpactOpen] = useState(false);
  const [reasonOpen, setReasonOpen] = useState(false);
  /** roleId → draft permission set (edit buffer). */
  const [draft, setDraft] = useState<Record<string, Set<string>>>({});
  /** roleId → draft `inheritsFrom` (rbacV2 edge edit buffer). */
  const [inheritsDraft, setInheritsDraft] = useState<Record<string, string[]>>({});

  const resetDraft = useMemo(
    () => (source: GovernanceRole[]) => {
      const next: Record<string, Set<string>> = {};
      source.forEach((role) => {
        next[role.id] = new Set(role.permissions);
      });
      return next;
    },
    []
  );

  const resetInheritsDraft = useMemo(
    () => (source: GovernanceRole[]) => {
      const next: Record<string, string[]> = {};
      source.forEach((role) => {
        next[role.id] = [...(role.inheritsFrom ?? [])];
      });
      return next;
    },
    []
  );

  useEffect(() => {
    if (roles) {
      setDraft(resetDraft(roles));
      setInheritsDraft(resetInheritsDraft(roles));
    }
  }, [roles, resetDraft, resetInheritsDraft]);

  const toggle = useCallback((roleId: string, permission: string, checked: boolean) => {
    setDraft((prev) => {
      const set = new Set(prev[roleId] ?? []);
      if (checked) set.add(permission);
      else set.delete(permission);
      return { ...prev, [roleId]: set };
    });
  }, []);

  const changeInherits = useCallback((roleId: string, inheritsFrom: string[]) => {
    setInheritsDraft((prev) => ({ ...prev, [roleId]: inheritsFrom }));
  }, []);

  // The SAME draft overlay the matrix renders from feeds the impact diff, so
  // what the modal previews is exactly what the save will commit (S3.3).
  const overlaidDraft = useMemo(
    () => overlayDraft(roles ?? [], draft, inheritsDraft),
    [roles, draft, inheritsDraft]
  );

  const cancelEdit = () => {
    if (roles) {
      setDraft(resetDraft(roles));
      setInheritsDraft(resetInheritsDraft(roles));
    }
    setEditMode(false);
  };

  const submitMatrix = async (reason: string) => {
    if (!roles) return;
    const permissionsChanged = (role: GovernanceRole) =>
      sortedJson(draft[role.id] ?? []) !== sortedJson(role.permissions);
    const inheritsChanged = (role: GovernanceRole) =>
      sortedJson(inheritsDraft[role.id] ?? []) !== sortedJson(role.inheritsFrom ?? []);
    const changed = roles.filter(
      (role) =>
        !role.isSystem && (permissionsChanged(role) || (rbacV2 && inheritsChanged(role)))
    );
    for (const role of changed) {
      if (rbacV2) {
        // Only changed pieces are sent: grants ride the reason-gated governance
        // PATCH (audit row unchanged), edges ride the S2.3 hierarchy endpoint.
        await setRoleDefinition(
          role.id,
          {
            ...(permissionsChanged(role)
              ? { permissions: [...(draft[role.id] ?? [])] }
              : {}),
            ...(inheritsChanged(role)
              ? { inheritsFrom: [...(inheritsDraft[role.id] ?? [])] }
              : {}),
          },
          reason
        );
      } else {
        await setRolePermissions(role.id, [...(draft[role.id] ?? [])], reason);
      }
    }
    message.success(t('messages.permissionsUpdated'));
    setReasonOpen(false);
    setEditMode(false);
    refresh();
  };

  // --- Legacy flat matrix (governance.rbacV2 OFF — byte-equivalent) ----------

  const legacyColumns: ColumnsType<PermissionRow> = [
    {
      title: t('fields.module'),
      dataIndex: 'moduleKey',
      width: 140,
      fixed: 'left',
      render: (moduleKey: string) => <Tag color="default">{t(`permissionModules.${moduleKey}`)}</Tag>,
    },
    {
      title: t('fields.permissionsCount'),
      dataIndex: 'permission',
      width: 200,
      fixed: 'left',
      render: (permission: string) => t(`permissions.${permissionLabelKey(permission)}`),
    },
    ...(roles ?? []).map((role) => ({
      title: <Text strong>{role.name}</Text>,
      key: role.id,
      align: 'center' as const,
      width: 150,
      render: (_: unknown, row: PermissionRow) => {
        const checked = role.isSystem ? true : Boolean(draft[role.id]?.has(row.permission));
        return (
          <Checkbox
            checked={checked}
            disabled={role.isSystem || !editMode}
            onChange={(e) => toggle(role.id, row.permission, e.target.checked)}
          />
        );
      },
    })),
  ];

  return (
    <div>
      <div className="tw-mb-4 tw-flex tw-items-start tw-justify-between">
        <div>
          <Text type="secondary" style={{ display: 'block' }}>
            {t('roles.hint')}
          </Text>
          <Text type="secondary" style={{ display: 'block' }}>
            {editMode ? t('roles.editHint') : t('roles.systemLocked')}
          </Text>
        </div>
        {editMode ? (
          <Space>
            <Button
              size="large"
              style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
              onClick={cancelEdit}
            >
              {t('actions.cancel')}
            </Button>
            <Button
              type="primary"
              size="large"
              style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
              // S3.3: rbacV2 saves review the impact diff first; the legacy
              // path keeps Save → reason byte-identical.
              onClick={() => (rbacV2 ? setImpactOpen(true) : setReasonOpen(true))}
            >
              {t('actions.save')}
            </Button>
          </Space>
        ) : (
          <Button
            size="large"
            icon={<EditOutlined />}
            style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
            onClick={() => setEditMode(true)}
          >
            {t('actions.edit')}
          </Button>
        )}
      </div>

      {rbacV2 ? (
        <PermissionMatrixGrid
          roles={roles ?? []}
          loading={loading}
          editMode={editMode}
          draftPermissions={draft}
          draftInherits={inheritsDraft}
          onTogglePermission={toggle}
          onChangeInherits={changeInherits}
        />
      ) : (
        <TableContainer>
          <Table<PermissionRow>
            rowKey="key"
            loading={loading}
            columns={legacyColumns}
            dataSource={PERMISSION_ROWS}
            pagination={false}
            scroll={{ x: 340 + (roles?.length ?? 0) * 150 }}
            locale={{ emptyText: <ListEmptyState title={t('empty.roles')} /> }}
          />
        </TableContainer>
      )}

      {rbacV2 && (
        <PermissionImpactModal
          open={impactOpen}
          originalRoles={roles ?? []}
          draftRoles={overlaidDraft}
          users={users ?? []}
          onConfirm={() => {
            setImpactOpen(false);
            setReasonOpen(true);
          }}
          onCancel={() => setImpactOpen(false)}
        />
      )}

      <ReasonRequiredModal
        open={reasonOpen}
        title={t('reasonModal.matrixTitle')}
        submitText={t('reasonModal.submit')}
        cancelText={t('reasonModal.cancel')}
        reasonLabel={t('reasonModal.reasonLabel')}
        requiredMessage={t('reasonModal.reasonRequired')}
        reasonPlaceholder={t('reasonModal.reasonPlaceholder')}
        onSubmit={submitMatrix}
        onCancel={() => setReasonOpen(false)}
      />
    </div>
  );
};

export default RolesTab;
